import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Crosshair } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGrid = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const yRadar = useTransform(scrollYProgress, [0, 1], ["0%", "55%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const [threat, setThreat] = useState(2.41);

  useEffect(() => {
    const t = setInterval(() => setThreat((2.2 + Math.random() * 0.6).toFixed(2)), 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y: yGrid }} className="absolute inset-0 grid-tactical" />
      <motion.div style={{ y: yRadar }} className="absolute -right-40 top-1/4 w-[640px] h-[640px] rounded-full border border-white/10 hidden lg:block">
        <div className="absolute inset-8 rounded-full border border-white/5" />
        <div className="absolute inset-20 rounded-full border border-white/5" />
        <div className="absolute inset-0 rounded-full radar-sweep" />
        <Crosshair className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-blue-500" />
      </motion.div>
      <div className="absolute left-1/3 -top-40 w-[560px] h-[560px] rounded-full bg-blue-600/10 blur-[140px] pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col justify-center max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-32 pb-16">
        <MaskedLine delay={0.1}>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-blue-500 font-semibold flex items-center gap-3 mb-8">
            <span className="w-2 h-2 bg-blue-500 pulse-dot" />
            Tactical Defense &amp; High-Assurance K9 Surveillance
          </span>
        </MaskedLine>

        <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-[5.5rem]">
          <MaskedLine delay={0.25}>Elite K9 Units.</MaskedLine>
          <MaskedLine delay={0.4}>Hardened Sites.</MaskedLine>
          <MaskedLine delay={0.55}>
            <span className="text-blue-500">Zero Compromise.</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg text-gray-400 leading-relaxed"
        >
          CCTV matrices, biometric fob entry, static and mobile guards, dual-purpose K9s and rapid
          alarm response — fused into one integrated defense doctrine for sites that cannot afford to fail.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-request-deployment-btn"
            onClick={() => scrollToSection("#quote")}
            className="clip-corners bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 flex items-center gap-3 transition-colors duration-300 group"
          >
            Request Deployment
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-view-services-btn"
            onClick={() => scrollToSection("#services")}
            className="clip-corners border border-white/20 hover:border-blue-500/60 hover:text-blue-400 text-gray-200 font-mono text-sm uppercase tracking-[0.2em] px-8 py-4 transition-colors duration-300"
          >
            View Capabilities
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-gray-500"
        >
          <span data-testid="hud-status" className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-emerald-400 pulse-dot" /> Status: Operational
          </span>
          <span data-testid="hud-response">Response: &lt;15 Min</span>
          <span data-testid="hud-k9">K9 Units: Active</span>
          <span data-testid="hud-threat" className="text-blue-400">Threat Index: {threat} ▲</span>
        </motion.div>
      </motion.div>

      <div className="relative z-10 border-t border-white/10 bg-[#0F1115]/70 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["120+", "Sites Secured"],
            ["34", "K9 Teams Fielded"],
            ["11 Min", "Avg Response"],
            ["24/7", "Command Desk"],
          ].map(([v, l], i) => (
            <div key={l} data-testid={`hero-stat-${i}`} className="flex items-baseline gap-3">
              <span className="font-display font-extrabold text-2xl text-gray-100">{v}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500">{l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
